import _ from 'lodash';
import { Injectable } from '@angular/core';
import { PublicClientApplication, Configuration, LogLevel, IPublicClientApplication, AuthenticationResult } from '@azure/msal-browser';
import { MicrosoftEntraIdAuthenticationResult } from './microsoft-entra-id-authentication-result';
import { MicrosoftEntraIdAuthenticationSettings } from 'src/app/store/authentication-settings/microsoft-entra-id-authentication-settings';

@Injectable({
   providedIn: 'root',
})
export class MicrosoftEntraIdService {
   private static readonly enableAutoMicrosoftEntraIdLogin = 'enableAutoMicrosoftEntraIdLogin';
   private readonly scopes = ['User.Read'];

   public static get isAutoMicrosoftEntraIdLoginEnabled(): boolean {
      return MicrosoftEntraIdService.getEnableAutoMicrosoftEntraIdLogin();
   }

   private static writeLocalStorage(key: string, value: string): void {
      try {
         localStorage.setItem(key, value);
      } catch (err) {
         console.error(`Failed to write ${key} to local storage.`, err);
      }
   }

   private static getEnableAutoMicrosoftEntraIdLogin(): boolean {
      try {
         const item = localStorage.getItem(MicrosoftEntraIdService.enableAutoMicrosoftEntraIdLogin) || 'false';
         return JSON.parse(item);
      } catch (err) {
         console.error(`Failed to read ${MicrosoftEntraIdService.enableAutoMicrosoftEntraIdLogin} from local storage.`, err);
      }
   }

   public async authenticate(settings: MicrosoftEntraIdAuthenticationSettings): Promise<MicrosoftEntraIdAuthenticationResult> {
      try {
         const app = this.init(settings);
         const result = await this.doAuthenticate(app);

         if (!_.isEmpty(result.accessToken)) {
            MicrosoftEntraIdService.writeLocalStorage(MicrosoftEntraIdService.enableAutoMicrosoftEntraIdLogin, 'true');
         }

         return result;
      } catch (error: any) {
         return { error: error };
      }
   }

   public async logout(settings: MicrosoftEntraIdAuthenticationSettings): Promise<void> {
      const app = this.init(settings);
      await app.logoutPopup();
   }

   public disableAutoMicrosoftEntraIdLogin(): void {
      MicrosoftEntraIdService.writeLocalStorage(MicrosoftEntraIdService.enableAutoMicrosoftEntraIdLogin, 'false');
   }

   private init(settings: MicrosoftEntraIdAuthenticationSettings): PublicClientApplication {
      const msalConfig = this.createMsalConfig(settings);
      return new PublicClientApplication(msalConfig);
   }

   private createMsalConfig(settings: MicrosoftEntraIdAuthenticationSettings): Configuration {
      return {
         auth: {
            clientId: settings.appClientId,
            authority: settings.authority,
            redirectUri: settings.redirectUri,
         },
         cache: {
            cacheLocation: settings.cacheLocation,
         },
         system: {
            loggerOptions: {
               loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
                  if (containsPii) {
                     return; // nothing to do
                  }

                  switch (level) {
                     case LogLevel.Error:
                        console.error(message);
                        break;
                     case LogLevel.Warning:
                        console.warn(message);
                        break;
                     default:
                        // ignore
                        break;
                  }
               },
               logLevel: LogLevel.Warning,
            },
         },
      };
   }

   private async doAuthenticate(app: IPublicClientApplication): Promise<MicrosoftEntraIdAuthenticationResult> {
      try {
         const authResponse = await app.ssoSilent({ scopes: this.scopes });
         return this.mapAuthenticationResult(authResponse);
      } catch (error: any) {
         const authResponse = await app.loginPopup({ scopes: this.scopes });
         return this.mapAuthenticationResult(authResponse);
      }
   }

   private mapAuthenticationResult(authenticationResult: AuthenticationResult): MicrosoftEntraIdAuthenticationResult {
      return {
         accessToken: authenticationResult.accessToken,
         idToken: authenticationResult.idToken,
      };
   }
}
